import * as React from 'react';
import { BriefcaseBusiness, Copy, GitBranch, Save, Trash2 } from 'lucide-react';
import { Combobox, type ComboboxOption } from '../../../components/ui/combobox';
import type {
  OrgModelEntity,
  PositionComputedState,
} from '../models/OrgModelProjection';
import type { PositionChartGroup } from '../services/positionChartGrouping';
import { positionChartSeatLabel } from '../services/positionChartGrouping';

export const PositionChartPropertiesPanel: React.FC<{
  group: PositionChartGroup | null;
  activePosition: OrgModelEntity | null;
  activeState: PositionComputedState | null;
  successionState: 'hidden' | 'unavailable' | 'visible';
  positions: readonly OrgModelEntity[];
  managerPositionId: string;
  disabled: boolean;
  onActivePositionChange: (positionId: string) => void;
  onManagerPositionChange: (positionId: string) => void;
  onSaveDetails: (details: { title: string; department?: string; level?: string }) => void;
  onApplyManager: () => void;
  onAddSeat: () => void;
  onRetire: () => void;
  onManageSuccession?: (positionId: string) => void;
}> = ({
  group,
  activePosition,
  activeState,
  successionState,
  positions,
  managerPositionId,
  disabled,
  onActivePositionChange,
  onManagerPositionChange,
  onSaveDetails,
  onApplyManager,
  onAddSeat,
  onRetire,
  onManageSuccession,
}) => {
  const [title, setTitle] = React.useState('');
  const [department, setDepartment] = React.useState('');
  const [level, setLevel] = React.useState('');

  // Department and Level are closed sets in practice — every value already in
  // use across the folder's positions — but planning legitimately needs to
  // introduce one that does not exist yet, so these are creatable comboboxes
  // rather than a `<select>`. Plain free text was fragmenting the taxonomy: one
  // typo silently splits a department away from its derived view.
  const departmentOptions = React.useMemo(
    () => toOptions(positions.map((position) => readString(position.props.department))),
    [positions],
  );
  const levelOptions = React.useMemo(
    () => toOptions(positions.map((position) =>
      readString(position.props.level ?? position.props.managementLevel))),
    [positions],
  );

  React.useEffect(() => {
    setTitle(activePosition?.label ?? '');
    setDepartment(readString(activePosition?.props.department) ?? '');
    setLevel(readString(activePosition?.props.level ?? activePosition?.props.managementLevel) ?? '');
  }, [activePosition]);

  if (!group || !activePosition) {
    return (
      <section className="border-b border-slate-200 px-4 py-4" aria-label="Position properties">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <BriefcaseBusiness className="size-4" />
          Position properties
        </div>
        <p className="mt-2 text-xs leading-5 text-slate-500">
          Select a position card to inspect its canonical seats and edit one seat at a time.
        </p>
      </section>
    );
  }

  const detailsChanged = title.trim() !== activePosition.label
    || department.trim() !== (readString(activePosition.props.department) ?? '')
    || level.trim() !== (readString(activePosition.props.level ?? activePosition.props.managementLevel) ?? '');

  const saveBlockedReason = disabled
    ? 'This chart is read-only. Use Make changes to edit seats.'
    : !title.trim()
      ? 'A position needs a title before it can be saved.'
      : !detailsChanged
        ? 'No changes to save yet.'
        : undefined;

  return (
    <section
      data-testid="position-chart-properties"
      className="border-b border-slate-200 bg-slate-50/50 px-4 py-3"
      aria-label="Position properties"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <BriefcaseBusiness className="size-3.5" />
            Position properties
          </div>
          <h2 className="mt-1 truncate text-sm font-semibold text-slate-900">{group.label}</h2>
        </div>
        <div className="shrink-0 rounded-full bg-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-700">
          {group.staffedCount === null
            ? `${group.approvedCount} seat${group.approvedCount === 1 ? '' : 's'}`
            : `${group.staffedCount}/${group.approvedCount} filled`}
        </div>
      </div>

      {group.positionIds.length > 1 ? (
        <label className="mt-3 block text-[11px] font-medium text-slate-600">
          Seat to inspect
          <select
            data-testid="position-group-seat-select"
            className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
            value={activePosition.id}
            onChange={(event) => onActivePositionChange(event.target.value)}
          >
            {group.positions.map((position) => {
              const state = position.id === activePosition.id
                ? activeState
                : null;
              return (
                <option key={position.id} value={position.id}>
                  {positionChartSeatLabel(position)}
                  {state?.primaryOccupantPersonId ? ' · filled' : ''}
                </option>
              );
            })}
          </select>
          <span className="mt-1 block text-[10px] leading-4 text-slate-400">
            This card groups matching seats for display. Edits still apply to one canonical Position.
          </span>
        </label>
      ) : null}

      <dl className="mt-3 grid grid-cols-[88px_1fr] gap-x-2 gap-y-1 text-[10px]">
        <dt className="text-slate-500">Position ID</dt>
        <dd className="truncate font-mono text-slate-700" title={activePosition.id}>
          {positionChartSeatLabel(activePosition)}
        </dd>
        <dt className="text-slate-500">Staffing</dt>
        <dd className="text-slate-700">
          {activeState?.occupancyVisibility === 'visible'
            ? activeState.staffingStatus
            : 'Unavailable'}
        </dd>
        {successionState !== 'hidden' && (
          <>
            <dt className="text-slate-500">Succession</dt>
            <dd className="text-slate-700">
              {activeState && successionState === 'visible'
                ? `${activeState.successorCount} successor${activeState.successorCount === 1 ? '' : 's'}`
                : 'Unavailable'}
            </dd>
          </>
        )}
      </dl>

      {successionState !== 'hidden' && onManageSuccession && (
        <button
          type="button"
          data-testid="position-manage-succession"
          className="mt-3 flex min-h-10 w-full items-center justify-center gap-2 rounded-md border border-violet-200 bg-violet-50 px-3 text-xs font-medium text-violet-800 hover:bg-violet-100"
          onClick={() => onManageSuccession(activePosition.id)}
        >
          <GitBranch className="size-3.5" />
          Manage succession in Full Org Chart
        </button>
      )}

      <div className="mt-3 space-y-2">
        <label className="block text-[11px] font-medium text-slate-600">
          Title
          <input
            className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-900"
            value={title}
            maxLength={160}
            disabled={disabled}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <div className="block text-[11px] font-medium text-slate-600">
            <label htmlFor="position-department">Department</label>
            <Combobox
              id="position-department"
              className="mt-1"
              triggerClassName="min-h-10 w-full text-xs font-normal text-slate-900"
              options={withClearOption(departmentOptions, department)}
              value={department}
              onValueChange={setDepartment}
              disabled={disabled}
              placeholder="Select department"
              searchPlaceholder="Search or add…"
              emptyText="No department matches."
              allowCustomValue
              createLabel={(query) => `Add department "${query}"`}
            />
          </div>
          <div className="block text-[11px] font-medium text-slate-600">
            <label htmlFor="position-level">Level</label>
            <Combobox
              id="position-level"
              className="mt-1"
              triggerClassName="min-h-10 w-full text-xs font-normal text-slate-900"
              options={withClearOption(levelOptions, level)}
              value={level}
              onValueChange={setLevel}
              disabled={disabled}
              placeholder="Select level"
              searchPlaceholder="Search or add…"
              emptyText="No level matches."
              allowCustomValue
              createLabel={(query) => `Add level "${query}"`}
            />
          </div>
        </div>
        <button
          type="button"
          className="flex w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-3 text-xs font-medium text-white disabled:bg-slate-300"
          disabled={disabled || !title.trim() || !detailsChanged}
          // A permanently greyed button with no reason reads as broken rather
          // than as "nothing to save".
          title={saveBlockedReason}
          onClick={() => onSaveDetails({
            title: title.trim(),
            department: department.trim() || undefined,
            level: level.trim() || undefined,
          })}
        >
          <Save className="size-3.5" />
          Save seat properties
        </button>
        {saveBlockedReason ? (
          <p className="text-[10px] leading-4 text-slate-500" role="status">{saveBlockedReason}</p>
        ) : null}
      </div>

      <div className="mt-3 border-t border-slate-200 pt-3">
        <label className="block text-[11px] font-medium text-slate-600">
          Reports to
          <div className="mt-1 flex gap-2">
            <select
              className="min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
              value={managerPositionId}
              disabled={disabled}
              onChange={(event) => onManagerPositionChange(event.target.value)}
            >
              <option value="">No manager (root)</option>
              {positions
                .filter((position) => position.id !== activePosition.id)
                .map((position) => (
                  <option key={position.id} value={position.id}>
                    {position.label} · {positionChartSeatLabel(position)}
                  </option>
                ))}
            </select>
            <button
              type="button"
              className="rounded-md border border-slate-300 bg-white px-2 text-xs font-medium text-slate-700 disabled:text-slate-400"
              disabled={disabled}
              onClick={onApplyManager}
            >
              Apply
            </button>
          </div>
        </label>
      </div>

      <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3">
        <button
          type="button"
          className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 text-xs font-medium text-slate-700 disabled:text-slate-400"
          disabled={disabled}
          onClick={onAddSeat}
        >
          <Copy className="size-3.5" />
          Add seat
        </button>
        <button
          type="button"
          className="flex items-center gap-1.5 rounded-md px-2 text-xs font-medium text-red-700 disabled:text-slate-400"
          disabled={disabled}
          onClick={onRetire}
        >
          <Trash2 className="size-3.5" />
          Retire seat
        </button>
      </div>
    </section>
  );
};

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** Distinct, sorted, non-empty values — the option set already in use. */
function toOptions(values: readonly (string | null)[]): ComboboxOption[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))]
    .sort((left, right) => left.localeCompare(right))
    .map((value) => ({ value, label: value }));
}

/**
 * Department and Level are optional. A text input could be emptied by deleting
 * its contents; a combobox needs somewhere to select "no value", so offer that
 * row only once there is something to clear.
 */
function withClearOption(options: ComboboxOption[], value: string): ComboboxOption[] {
  return value.trim() ? [{ value: '', label: 'Clear' }, ...options] : options;
}
